import { get_euler_angles_from_rotation_matrix } from '../../../../Math.js';
import { IColor4, IMatrix4, IVector3 } from '../../../../api.js';
import { assert } from '../../../../babylon/utility/Assert.js';
import { GIZMO_AXIS_COLOR, GIZMO_COLOR_FACTOR } from '../GizmoColors.js';
import { MeshDescriptions } from '../MeshDescriptions.js';
export class RotationTorus {
    constructor(viewer, transform_handling, use_global_transorm, axis) {
        this.transform_handling = transform_handling;
        this.use_global_transorm = use_global_transorm;
        this.axis = axis;
        this._initial_plane_point = null;
        this._viewer = viewer;
        const color = GIZMO_AXIS_COLOR[axis];
        const torus_mesh_description = MeshDescriptions.create_torus(0.85, // Major radius
        0.02, // Minor radius
        60, // Major segments
        8, // Minor Segments
        color);
        const input_mesh_description = MeshDescriptions.create_circle(1, 0.7, 30, new IColor4(0, 0, 0, 0));
        this._ui_mesh = this._viewer.create_ui_mesh();
        this._ui_mesh.load_mesh(input_mesh_description);
        this.gizmo = this._ui_mesh.create_gizmo();
        this._torus_mesh = this._viewer.create_ui_mesh();
        this._torus_mesh.load_mesh(torus_mesh_description);
        this._torus = this._torus_mesh.create_instance();
        this._torus.color_factor = GIZMO_COLOR_FACTOR.not_active;
        this._torus.set_parent(this.gizmo);
        if (axis === 'x') {
            this.gizmo.rotation.z = 90 * (Math.PI / 180);
        }
        else if (axis === 'z') {
            this.gizmo.rotation.x = -90 * (Math.PI / 180);
        }
        this.add_event_listeners();
        this._ui_mesh.draw_on_top = true;
        this._torus_mesh.draw_on_top = true;
    }
    dispose() {
        this._torus.dispose();
        this.gizmo.dispose();
    }
    add_event_listeners() {
        this.gizmo.pointer_enter_observable.add(this.on_pointer_over.bind(this));
        this.gizmo.drag_begin_observable.add(this.on_drag_start.bind(this));
        this.gizmo.drag_observable.add(this.on_drag.bind(this));
        this.gizmo.drag_end_observable.add(this.on_drag_end.bind(this));
        this.gizmo.pointer_leave_observable.add(this.on_pointer_leave.bind(this));
    }
    on_pointer_over() {
        if (this._initial_plane_point)
            return;
        this._torus.color_factor = GIZMO_COLOR_FACTOR.active;
    }
    on_pointer_leave() {
        if (this._initial_plane_point)
            return;
        this._torus.color_factor = GIZMO_COLOR_FACTOR.not_active;
    }
    on_drag_start(_, e) {
        this._initial_transform = this.transform_handling.get_world_transform();
        this._initial_plane_point = e.plane_point;
        this._torus.color_factor = GIZMO_COLOR_FACTOR.active;
    }
    on_drag(_, e) {
        if (!this._initial_plane_point || !this._initial_transform)
            return;
        const current_plane_point = e.plane_point;
        const center = this.get_parent().position;
        // Get the axis of rotation
        let rotation_axis;
        if (this.use_global_transorm) {
            switch (this.axis) {
                case 'x':
                    rotation_axis = IVector3.Right();
                    break;
                case 'y':
                    rotation_axis = IVector3.Up();
                    break;
                case 'z':
                    rotation_axis = IVector3.Forward();
                    break;
            }
        }
        else {
            const object_transform = IMatrix4.extractRotationMatrix(this._initial_transform);
            switch (this.axis) {
                case 'x':
                    rotation_axis = IMatrix4.transform_vector3(object_transform, IVector3.Right());
                    break;
                case 'y':
                    rotation_axis = IMatrix4.transform_vector3(object_transform, IVector3.Up());
                    break;
                case 'z':
                    rotation_axis = IMatrix4.transform_vector3(object_transform, IVector3.Forward());
                    break;
            }
        }
        // Project the points on plane
        const start_vector = IVector3.project_on_plane(IVector3.subtract(this._initial_plane_point, center), rotation_axis);
        const current_vector = IVector3.project_on_plane(IVector3.subtract(current_plane_point, center), rotation_axis);
        const angle = IVector3.angle(start_vector, current_vector);
        // Get the sign of the angle
        const cross_product = IVector3.cross(start_vector, current_vector);
        const sign = IVector3.dot(cross_product, rotation_axis) > 0 ? -1 : 1;
        const rotation_angle = angle * sign;
        this.apply_rotation(rotation_angle);
    }
    on_drag_end(_, e) {
        this._initial_plane_point = null;
        this._torus.color_factor = GIZMO_COLOR_FACTOR.not_active;
    }
    get_parent() {
        return this.gizmo['parent'];
    }
    apply_rotation(angle_delta) {
        var _a, _b;
        assert(this._initial_transform, 'Initial transform should be set');
        const world_matrix = this._initial_transform;
        const parent_matrix = (_b = (_a = this.transform_handling).get_parent_world_transform) === null || _b === void 0 ? void 0 : _b.call(_a);
        let rotation_matrix;
        switch (this.axis) {
            case 'x':
                rotation_matrix = IMatrix4.Rotation_X(angle_delta);
                break;
            case 'y':
                rotation_matrix = IMatrix4.Rotation_Y(angle_delta);
                break;
            case 'z':
                rotation_matrix = IMatrix4.Rotation_Z(angle_delta);
                break;
        }
        const new_rotation_matrix = this.use_global_transorm ? IMatrix4.multiply(rotation_matrix, world_matrix) : IMatrix4.multiply(world_matrix, rotation_matrix);
        if (parent_matrix) {
            const inverted_parent_transform = IMatrix4.inverse(parent_matrix);
            const local_rotation_matrix = IMatrix4.multiply(inverted_parent_transform, new_rotation_matrix);
            const new_rotation = get_euler_angles_from_rotation_matrix(local_rotation_matrix);
            this.transform_handling.rotation.set(new_rotation);
            return;
        }
        const new_rotation = get_euler_angles_from_rotation_matrix(new_rotation_matrix);
        this.transform_handling.rotation.set(new_rotation);
    }
}
//# sourceMappingURL=RotationTorus.js.map